import { SelectAttributesManager } from './SelectAttributesManager';
import { getTreeSelectView, getTreeSelectedView } from './utils';
import {
  AttributesAndBundlesModel,
  BundleAndAttributesInputProps,
  DataNode,
  FormLayout,
} from '../common';
import { formItemLayout } from '../../../common';
import { ProgramWizardStepDataProcessor } from '../common/ProgramWizardStepDataProcessor';
import { useDataPart, useStepContext } from '@/app/core/components/step-wizard';
import {
  useState,
  useEffect,
  useRef,
  Fragment,
  MutableRefObject,
  ChangeEvent,
} from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import {
  FullAttribute,
  MaxAllowedAttributes,
  MaxAllowedScoreDistributioReportsAttributes,
  ReportsAttributeLike,
  reportsInfoValidator,
  AttributeLike,
  areAttributesSame,
} from '@ssar-app/domain';
import RcTree from 'rc-tree';
import { Tree, Alert } from '@ssar-app/uikit';
import {
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  InputGroup,
  InputRightElement,
  Stack,
  Checkbox,
  Text,
  Input,
  Flex,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa/index.js';
import { ObjectSchema } from 'yup';

export const SelectAttributesAndBundles = (
  props: BundleAndAttributesInputProps,
) => {
  const { stepKey, validator } = props;
  const { lookupsManager, additionalKey } = props.moduleProps;

  const { isStepActive } = useStepContext();
  const [dataPartState] = useDataPart<AttributesAndBundlesModel>(stepKey);
  const [reportsDataPartState] =
    useDataPart<ReportsAttributeLike>(additionalKey);
  const [model, setModel] = useState<Partial<AttributesAndBundlesModel>>(
    dataPartState.data || {},
  );
  const [reportsModel, setReportsModel] = useState<
    Partial<ReportsAttributeLike>
  >(reportsDataPartState.data || {});
  const formMethods = useForm<AttributesAndBundlesModel>({
    defaultValues: model,
    mode: 'onChange',
    resolver: yupResolver(validator),
  });

  const { control, formState, trigger } = formMethods;

  const selectableTreeRef = useRef<RcTree>(null);
  const selectedTreeRef = useRef<RcTree>(null);
  const listOutOfSelectedItemsRef = useRef<RcTree>(null);
  const disabledAttributesLookups =
    lookupsManager.getDisableAttributesLookups();
  const attributes: AttributeLike[] =
    model?.attributes?.filter(
      (a) => !disabledAttributesLookups.some((b) => areAttributesSame(a, b)),
    ) || [];

  const selectedAttributes: AttributeLike[] =
    reportsModel?.distribution?.attributes?.filter(
      (a) => !disabledAttributesLookups.some((b) => areAttributesSame(a, b)),
    ) || [];

  const attributesManagerRef = useRef(
    new SelectAttributesManager(
      lookupsManager.getAttributesLookups(),
      attributes,
    ),
  ); // To store master list
  const selectedAttributesManagerRef = useRef(
    new SelectAttributesManager(
      lookupsManager.getAttributesLookups(),
      selectedAttributes,
    ),
  );

  const [selectableAttributeItems, setSelectableAttributeItems] = useState<
    DataNode[]
  >([]); // Used in Select Tree to show master data in Tree View

  const [selectedAttributeItems, setSelectedAttributeItems] = useState<
    DataNode[]
  >([]); // Used in Selected tree to show selected data in Tree View

  const [bundleListattributes, setBundleListattributes] = useState<DataNode[]>(
    [],
  );
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [finalCheckedKeys, setFinalCheckedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<(string | number)[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);

  const disabledBundlesLookups = lookupsManager.getDisableBundlesLookupsCodes();
  const bundles: string[] =
    model?.bundles?.filter((a) => !disabledBundlesLookups.includes(a)) || [];

  const fullAttributesLookupsFromManager =
    lookupsManager.getFullAttributesLookups();
  const fullAttributesLookups = fullAttributesLookupsFromManager.map(
    (fullAttribute) => fullAttribute.code,
  );
  const fullAttributes: FullAttribute[] =
    model?.fullAttributes?.filter((fa) =>
      fullAttributesLookups.includes(fa.code),
    ) || [];

  useEffect(() => {
    bundleOnChange(bundles);
    fullAttributeOnChange(fullAttributes);
    formMethods.register({ name: 'attributes', type: 'custom' });
    formMethods.register({ name: 'reports', type: 'custom' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTreeViews = (
    attributesReference: MutableRefObject<SelectAttributesManager>,
    afterSelectingAttributes: boolean,
    scrollTreesToTop?: boolean,
  ): void => {
    if (afterSelectingAttributes) {
      setSelectedAttributeItems(
        getTreeSelectedView(
          attributesManagerRef.current,
          removeAttrClicked,
          false,
          selectedAttributesManagerRef.current,
          true,
        ),
      );
      setBundleListattributes(
        getTreeSelectedView(
          attributesReference.current,
          removeAttrClicked,
          true,
          selectedAttributesManagerRef.current,
          true,
        ),
      );
      setFinalCheckedKeys([
        ...attributesReference.current.getFinalCheckedKeysArray(),
      ]);
    } else {
      setSelectableAttributeItems(
        getTreeSelectView(attributesReference.current, true),
      );
      setSelectedAttributeItems(
        getTreeSelectedView(
          attributesReference.current,
          removeAttrClicked,
          false,
          selectedAttributesManagerRef.current,
          false,
        ),
      );
      setCheckedKeys([
        ...attributesReference.current.getFinalCheckedKeysArray(),
      ]);

      if (scrollTreesToTop) {
        setTimeout(() => {
          selectableTreeRef.current?.scrollTo({
            key: selectableAttributeItems[0]?.key || '',
          });
          selectedTreeRef.current?.scrollTo({
            key: selectedAttributeItems[0]?.key || '',
          });
        }, 450);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onCheck = (checkedKeys: any, info: any): void => {
    if (info.checked) {
      attributesManagerRef.current.onCheckAttributes(info.checkedNodes);
      setTreeViews(attributesManagerRef, false);
    } else {
      removeAttrClicked(info.node.key, false);
      removeAttrClicked(info.node.key, true);
    }
  };

  const removeAttrClicked = (
    key: string,
    forSelectedListTree: boolean,
  ): void => {
    if (forSelectedListTree) {
      selectedAttributesManagerRef.current.onRemoveAttributes(key);
      setTreeViews(selectedAttributesManagerRef, true);
    } else {
      attributesManagerRef.current.onRemoveAttributes(key);
      selectedAttributesManagerRef.current.onRemoveAttributes(key);
      setTreeViews(attributesManagerRef, false);
      setTreeViews(selectedAttributesManagerRef, true);
    }
  };

  const onSearch = (
    e: ChangeEvent<HTMLInputElement>,
    forSelectableTree: boolean,
  ): void => {
    if (forSelectableTree) {
      const value = e.target.value.trim().toLowerCase();
      setAutoExpandParent(true);
      if (value.length > 2) {
        setExpandedKeys([...attributesManagerRef.current.getMiddleKeys()]);
      } else {
        setExpandedKeys([]);
      }
      attributesManagerRef.current.onSearchAttributes(value);
      setTreeViews(attributesManagerRef, false, true);
    } else {
      const value = e.target.value.trim().toLowerCase();
      selectedAttributesManagerRef.current.onSearchAttributes(value);
      setTreeViews(selectedAttributesManagerRef, true, true);
    }
  };

  const bundleOnChange = (values: (string | number | boolean)[]): void => {
    const stringValues: string[] = values
      .filter((a) => typeof a === 'string')
      .map((a) => a.toString());

    const attributesForBundle =
      attributesManagerRef.current.getAttributesForBundle(
        stringValues,
        lookupsManager.getBundleAttributes(),
      );

    attributesManagerRef.current.onBundleChangeAttributes(attributesForBundle);
    setTreeViews(attributesManagerRef, false, true);
    setTreeViews(selectedAttributesManagerRef, true, true);

    setModel({
      ...model,
      bundles: stringValues,
    });
  };

  const fullAttributeOnChange = (values: FullAttribute[]): void => {
    const stringValues: string[] = values?.map((a) => a?.code) || [];

    const attributesForFullAttribute =
      attributesManagerRef.current.getAttributesForFullAttribute(
        stringValues,
        lookupsManager.getAttributesFromFullAttribute(),
      );

    attributesManagerRef.current.onFullAttributeChangeAttributes(
      attributesForFullAttribute,
    );
    selectedAttributesManagerRef.current.onFullAttributeChangeAttributes(
      attributesForFullAttribute,
    );

    setTreeViews(attributesManagerRef, false);
    setTreeViews(selectedAttributesManagerRef, true);
    setModel({
      ...model,
      fullAttributes: values,
    });
  };

  const onExpand = (expandedKeys: (string | number)[]): void => {
    setExpandedKeys(expandedKeys);
    setAutoExpandParent(false);
  };

  useEffect(() => {
    const checkedAttributes =
      attributesManagerRef.current.getCheckedUnpackedKeys();
    formMethods.setValue('attributesAndBundles.attributes', checkedAttributes);

    setModel({
      ...model,
      attributes: checkedAttributes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedKeys]);

  useEffect(() => {
    const newAttributes =
      selectedAttributesManagerRef.current.getCheckedUnpackedKeys();
    formMethods.setValue('reports', {
      distribution: {
        attributes: newAttributes,
      },
    });

    setReportsModel({
      ...reportsModel,
      distribution: {
        ...reportsModel.distribution,
        attributes: newAttributes,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalCheckedKeys]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSelectedAttributesCheck = (checkedKeys: any, info: any): void => {
    if (info.checked) {
      selectedAttributesManagerRef.current.onCheckAttributes(info.checkedNodes);
      setTreeViews(selectedAttributesManagerRef, true);
    } else {
      removeAttrClicked(info.node.key, true);
    }
  };

  const [fullAttributeDetails, setFullAttributeDetails] = useState<{
    [key: string]: FullAttribute;
  }>({});

  useEffect(() => {
    setFullAttributeDetails((details) => {
      details = {};
      fullAttributesLookupsFromManager.map((fa) => (details[fa.code] = fa));
      return details;
    });
  }, [fullAttributesLookupsFromManager]);

  return (
    <Fragment>
      <ProgramWizardStepDataProcessor
        stepKey={stepKey}
        trigger={trigger}
        triggerFieldNames={['attributes', 'bundles', 'fullAttributes']}
        validator={validator as ObjectSchema}
        model={model}
        isDirty={formState.isDirty}
      />
      <ProgramWizardStepDataProcessor
        stepKey={additionalKey}
        trigger={trigger}
        triggerFieldNames={['reports']}
        validator={reportsInfoValidator as ObjectSchema}
        model={reportsModel}
        isDirty={formState.isDirty}
      />
      {isStepActive && (
        <Flex direction='column' w='full'>
          <form {...formItemLayout}>
            <FormLayout header={'Bundles'}>
              <FormControl>
                <Controller
                  name='attributesAndBundles.bundles'
                  control={control}
                  defaultValue={bundles}
                  render={({ value, onChange }) => {
                    return (
                      <CheckboxGroup
                        value={value}
                        onChange={(values) => {
                          bundleOnChange(values);
                          onChange(values);
                        }}
                      >
                        <Stack spacing={2}>
                          {lookupsManager.getBundlesLookups().map((a) => (
                            <Fragment key={a.code}>
                              <Checkbox
                                value={a.code}
                                mt={0}
                                borderColor={'gray.300'}
                              >
                                {a.name}
                              </Checkbox>
                            </Fragment>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                    );
                  }}
                ></Controller>
              </FormControl>
            </FormLayout>

            <FormLayout header={'Complete Attribute Sets'}>
              <FormControl>
                <Controller
                  name='attributesAndBundles.fullAttributes'
                  control={control}
                  defaultValue={fullAttributes.map((fa) => fa.code)}
                  render={({ value, onChange }) => {
                    return (
                      <CheckboxGroup
                        value={value}
                        onChange={(values) => {
                          const details = Object.keys(fullAttributeDetails)
                            .filter((fa) => values.includes(fa))
                            .map((fa) => fullAttributeDetails[fa]);
                          formMethods.setValue(
                            'attributesAndBundles.fullAttributes',
                            details,
                          );
                          fullAttributeOnChange(details);
                          onChange(details.map((fa) => fa.code));
                        }}
                      >
                        {fullAttributesLookupsFromManager.length ? (
                          <Stack spacing={2}>
                            {fullAttributesLookupsFromManager.map((a) => (
                              <Fragment key={a.code}>
                                <Checkbox
                                  value={a.code}
                                  mt={0}
                                  borderColor={'gray.300'}
                                >
                                  {a.displayName} Attributes
                                </Checkbox>
                              </Fragment>
                            ))}
                          </Stack>
                        ) : (
                          'There are no Complete Attribute Sets available for selection.'
                        )}
                      </CheckboxGroup>
                    );
                  }}
                ></Controller>
              </FormControl>
            </FormLayout>

            <FormLayout header={'Attributes'}>
              <Grid
                templateRows='repeat(1, 1fr)'
                templateColumns='repeat(6, 1fr)'
                gap='5'
                w='full'
              >
                <GridItem colSpan={2} rowSpan={1}>
                  <Text fontSize='md'>{`Available Attributes (select up to ${MaxAllowedAttributes})`}</Text>
                  <InputGroup size='sm' width='80' mt={2} rounded='md'>
                    <Input
                      variant='outline'
                      placeholder='Search Attributes'
                      onChange={(e) => onSearch(e, true)}
                      mb={2}
                      spellCheck={false}
                    />
                    <InputRightElement>
                      <FaSearch />
                    </InputRightElement>
                  </InputGroup>
                  <ErrorMessage
                    errors={formMethods.errors}
                    name='attributes'
                    render={({ message }) => (
                      <Text color={'red'}>{message}</Text>
                    )}
                  />
                  <Tree
                    checkable
                    checkedKeys={checkedKeys}
                    onExpand={onExpand}
                    expandedKeys={expandedKeys}
                    autoExpandParent={autoExpandParent}
                    treeData={selectableAttributeItems}
                    onCheck={onCheck}
                    selectable={false}
                    height={500}
                    ref={selectableTreeRef}
                  />
                </GridItem>
                <GridItem colSpan={2} rowSpan={1}>
                  <Text>{`Selected Attributes (${
                    attributesManagerRef.current.getCheckedKeysSet().size
                  })`}</Text>
                  <InputGroup size='sm' width='80' mt={2} rounded='md'>
                    <Input
                      variant='outline'
                      placeholder='Search'
                      onChange={(e) => onSearch(e, false)}
                      mb={2}
                      spellCheck={false}
                    />
                    <InputRightElement>
                      <FaSearch />
                    </InputRightElement>
                  </InputGroup>
                  <ErrorMessage
                    errors={formMethods.errors}
                    name='attributes'
                    render={({ message }) => (
                      <Text color={'red'}>{message}</Text>
                    )}
                  />
                  <Tree
                    checkable
                    treeData={selectedAttributeItems}
                    selectable={false}
                    height={500}
                    ref={selectedTreeRef}
                    onCheck={onSelectedAttributesCheck}
                    checkedKeys={finalCheckedKeys}
                  />
                  <FormErrorMessage>Required</FormErrorMessage>
                </GridItem>
                <GridItem colSpan={2} rowSpan={1}>
                  <Text fontSize='md'>{`Included in distribution report (${
                    selectedAttributesManagerRef.current.getCheckedKeysSet()
                      .size
                  } / ${MaxAllowedScoreDistributioReportsAttributes})`}</Text>
                  <Alert
                    description={
                      selectedAttributesManagerRef.current.getCheckedKeysSet()
                        .size > 0
                        ? `You have selected ${
                            selectedAttributesManagerRef.current.getCheckedKeysSet()
                              .size
                          } attributes to include in Distribution report`
                        : `To include attributes in the distribution report, please select from the selected column of attributes.`
                    }
                    type='info'
                    showIcon
                    backgroundColor={'white'}
                    mt={1}
                    mb={2}
                    isOpen={true}
                    showCloseButton={false}
                    chakraAlertProps={{ borderWidth: '1px' }}
                  />
                  <Tree
                    treeData={bundleListattributes}
                    selectable={false}
                    height={500}
                    ref={listOutOfSelectedItemsRef}
                    autoExpandParent={true}
                    onCheck={onSelectedAttributesCheck}
                  />
                </GridItem>
              </Grid>
            </FormLayout>
          </form>
        </Flex>
      )}
    </Fragment>
  );
};
